import { useMemo } from 'react'
import { Card } from '@/components/ui/card'
import { Loader2, AlertCircle, Shield } from 'lucide-react'
import { useVisiblePolling } from '@/hooks/useVisiblePolling'
import { zanshinService } from '@/services/zanshin.service'
import { ZanshinPairCard } from '@/components/zanshin/ZanshinPairCard'

export function ZanshinMonitor() {
  const { data, error, isLoading } = useVisiblePolling(
    zanshinService.getAdminPairsStatus,
    3000,
  )
  const { data: workerStatus } = useVisiblePolling(
    zanshinService.getStatus,
    5000,
  )

  // Raggruppa pair per user_id (vista admin globale piu' leggibile)
  const grouped = useMemo(() => {
    if (!data?.pairs) return []
    const map = new Map<number, typeof data.pairs>()
    for (const p of data.pairs) {
      const uid = p.user_id ?? 0
      if (!map.has(uid)) map.set(uid, [])
      map.get(uid)!.push(p)
    }
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0])
  }, [data])

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-400" />
            Zanshin Monitor
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Stato del trailing engine per tutte le coppie attive
          </p>
        </div>
        {data && (
          <div className="text-xs text-muted-foreground text-right">
            <div>Server: {new Date(data.server_ts).toLocaleTimeString('it-IT')}</div>
            <div className="mt-0.5">Polling 3s · si ferma quando il tab non e' visibile</div>
          </div>
        )}
      </div>

      {/* ── Worker health bar ──────────────────────────────────────────── */}
      {workerStatus && (
        <Card className="p-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
          <span className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${workerStatus.worker.enabled && workerStatus.worker.task_alive ? 'bg-green-500' : 'bg-red-500'}`} />
            Worker: <strong>{workerStatus.worker.enabled ? (workerStatus.worker.task_alive ? 'attivo' : 'crashed') : 'disabilitato'}</strong>
          </span>
          <span>Tick: <strong>{workerStatus.worker.tick_seconds}s</strong></span>
          <span>Pending: <strong>{workerStatus.worker.pending_count}</strong></span>
          <span>Locks: <strong>{workerStatus.worker.pair_locks_count}</strong></span>
          <span className={workerStatus.worker.consecutive_errors > 0 ? 'text-orange-400' : ''}>
            Errori consecutivi: <strong>{workerStatus.worker.consecutive_errors}</strong>
          </span>
          <span className="ml-auto">
            FORCE_CLOSE 24h: <strong className={workerStatus.totals.force_close_24h > 0 ? 'text-red-400' : ''}>
              {workerStatus.totals.force_close_24h}
            </strong>
          </span>
        </Card>
      )}

      {/* ── Totali ─────────────────────────────────────────────────────── */}
      {data && (
        <div className="grid grid-cols-3 gap-3">
          <Card className="p-3 text-center">
            <div className="text-2xl font-bold">{data.totals.total}</div>
            <div className="text-xs text-muted-foreground">Coppie attive</div>
          </Card>
          <Card className="p-3 text-center">
            <div className="text-2xl font-bold text-green-400">{data.totals.with_op}</div>
            <div className="text-xs text-muted-foreground">Con operazione</div>
          </Card>
          <Card className="p-3 text-center">
            <div className={`text-2xl font-bold ${data.totals.pending > 0 ? 'text-amber-400' : ''}`}>
              {data.totals.pending}
            </div>
            <div className="text-xs text-muted-foreground">In attesa EA</div>
          </Card>
        </div>
      )}

      {/* ── Errore ─────────────────────────────────────────────────────── */}
      {error && (
        <Card className="p-4 border-red-800 bg-red-950/30 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="text-red-300 font-medium">Errore caricamento dati</div>
            <div className="text-red-400/80 text-xs mt-0.5">{error.message}</div>
          </div>
        </Card>
      )}

      {/* ── Loading iniziale ───────────────────────────────────────────── */}
      {isLoading && !data && (
        <Card className="p-8 flex items-center justify-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento coppie...
        </Card>
      )}

      {/* ── Lista pair raggruppate per user ────────────────────────────── */}
      {data && data.pairs.length === 0 && (
        <Card className="p-8 text-center text-muted-foreground">
          Nessuna coppia attiva nel sistema.
        </Card>
      )}
      {grouped.map(([userId, pairs]) => (
        <div key={userId} className="space-y-3">
          <div className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
            Utente #{userId} · {pairs.length} {pairs.length === 1 ? 'coppia' : 'coppie'}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {pairs.map(p => (
              <ZanshinPairCard key={p.pair_id} pair={p} showUserId={false} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
